import { useRef, useState } from "react";
import { uploadEventImage, deleteEventImage, ApiError } from "../api";

/**
 * Per-event header image uploader — the event-calendar mirror of
 * BannerUploader. Drop or pick → center-crop to 16:9 → resize to
 * 1600×900 JPEG @ 80% → upload to R2 via the worker's event-image
 * endpoint. Renders a hero on the rider's event sheet exactly like the
 * trail banner does on the trail sheet.
 *
 * Unlike BannerUploader (which fetches the trail to learn its current
 * banner) an event's current image is already in hand from the events
 * list, so we take it as a prop and report changes back via onChange.
 */

const OUTPUT_WIDTH = 1600;
const OUTPUT_HEIGHT = 900;
const MAX_RAW_BYTES = 12 * 1024 * 1024;
const ACCEPTED = "image/png,image/jpeg,image/webp";

export function EventImageUploader({
  eventId,
  initialUrl,
  onChange,
  compact = false,
}: {
  eventId: string;
  initialUrl?: string | null;
  onChange?: (url: string | null) => void;
  /** When true, drops the section chrome (heading + blurb) so the
   *  uploader can live inline inside an event card's edit strip. */
  compact?: boolean;
}) {
  const [currentUrl, setCurrentUrl] = useState<string | null>(initialUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [phase, setPhase] = useState<"idle" | "cooking" | "uploading" | "done">("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function pick() {
    fileInputRef.current?.click();
  }

  async function processAndUpload(file: File) {
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("That doesn't look like an image. JPEG, PNG, or WebP please.");
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      setError("File is over 12 MB. Pick something smaller.");
      return;
    }
    setUploading(true);
    setPercent(0);
    setPhase("cooking");
    try {
      const blob = await renderImageBlob(file, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      setPhase("uploading");
      const r = await uploadEventImage(eventId, blob, OUTPUT_WIDTH, OUTPUT_HEIGHT, (p) =>
        setPercent(p)
      );
      setCurrentUrl(r.image.url);
      onChange?.(r.image.url);
      setPhase("done");
      setTimeout(() => {
        setPhase("idle");
        setUploading(false);
        setPercent(0);
      }, 900);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed.");
      setPhase("idle");
      setUploading(false);
      setPercent(0);
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processAndUpload(file);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processAndUpload(file);
  }

  async function handleDelete() {
    if (!confirm("Remove this event's header image?")) return;
    try {
      await deleteEventImage(eventId);
      setCurrentUrl(null);
      onChange?.(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't remove the image.");
    }
  }

  return (
    <div className={compact ? "event-photo-uploader" : "admin-section"}>
      {!compact && <div className="admin-section-heading">Event image</div>}
      <div className={compact ? "event-photo-uploader-body" : "admin-section-body"}>
        {!compact && (
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            Optional hero photo shown at the top of the event's detail sheet.
            Wide landscape shots work best — we'll center-crop and resize to
            {" "}{OUTPUT_WIDTH}×{OUTPUT_HEIGHT}.
          </p>
        )}

        {currentUrl ? (
          <div className="banner-preview-wrap">
            <img className="banner-preview" src={currentUrl} alt="Current event image" />
            <div className="row" style={{ gap: 8, marginTop: 10 }}>
              <button type="button" className="small secondary" onClick={pick} disabled={uploading}>
                Replace
              </button>
              <button type="button" className="small ghost" onClick={handleDelete} disabled={uploading}>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`banner-dropzone ${dragOver ? "is-over" : ""}`}
            onClick={pick}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            <div className="banner-dropzone-copy">
              <strong>Drop a photo here</strong>
              <span className="muted" style={{ fontSize: 12 }}>
                or click to pick from your computer
              </span>
            </div>
          </div>
        )}

        {uploading && (
          <div className="banner-progress">
            <div
              className="banner-progress-fill"
              style={{ width: `${Math.max(4, percent)}%` }}
            />
            <span className="banner-progress-label">
              {phase === "cooking"
                ? "Cooking it up…"
                : phase === "uploading"
                  ? `Uploading… ${percent}%`
                  : phase === "done"
                    ? "Looks epic ✨"
                    : ""}
            </span>
          </div>
        )}

        {error && <div className="admin-feedback admin-feedback-err">{error}</div>}

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          onChange={onFileInput}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
}

/**
 * Center-crop the source image to 16:9 and resize to outputW × outputH,
 * exported as JPEG @ 0.8. Identical to BannerUploader's renderBannerBlob.
 */
async function renderImageBlob(
  file: File,
  outputW: number,
  outputH: number
): Promise<Blob> {
  const image = await loadImage(URL.createObjectURL(file));
  const targetAspect = outputW / outputH;
  const srcAspect = image.width / image.height;

  let sx = 0, sy = 0, sw = image.width, sh = image.height;
  if (srcAspect > targetAspect) {
    sw = image.height * targetAspect;
    sx = (image.width - sw) / 2;
  } else if (srcAspect < targetAspect) {
    sh = image.width / targetAspect;
    sy = (image.height - sh) / 2;
  }

  const canvas = document.createElement("canvas");
  canvas.width = outputW;
  canvas.height = outputH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outputW, outputH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Failed to encode the image"));
        resolve(blob);
      },
      "image/jpeg",
      0.8
    );
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't decode the image"));
    img.src = src;
  });
}
